import React from "react"
import { useTranslation } from "react-i18next"
import { type ButtonIcon, ButtonDisplayVariant } from "../../types/buttonCustomization"
import { getButtonIconSVG } from "../ButtonIconSVGs"

interface ButtonPreviewProps {
  color: string
  text: string
  icon: ButtonIcon
  showText: boolean
  position: ButtonDisplayVariant
  hasNoIcon?: boolean
  iconTextSameLine?: boolean
  textColor?: string
  borderRadius?: string
  borderWidth?: string
  borderColor?: string
}

export const ButtonPreview: React.FC<ButtonPreviewProps> = ({
  color,
  text,
  icon,
  showText,
  position,
  hasNoIcon = false,
  iconTextSameLine = false,
  textColor = "#FFFFFF",
  borderRadius,
  borderWidth,
  borderColor,
}) => {
  const { i18n } = useTranslation()

  const getDefaultText = () => {
    const translations: Record<string, string> = {
      en: "Virtual try on",
      pt: "Prova Virtual",
    }
    return translations[i18n.language] || translations.en
  }

  const displayText = text || getDefaultText()

  // Scale border radius and width to match visual appearance between preview (scaled 3x) and deployed
  // Admin: width 56px -> Deployed: width 73px (ratio: 1.3x)
  // Admin: border 2px -> Deployed: border 13px (ratio: 6.5x)
  // Border scales differently than radius - use different scaling
  const scaleBorderRadius = (value: string | undefined) => {
    if (!value || value === "") return undefined
    const match = value.match(/(\d+\.?\d*)(px|%)/)
    if (!match) return value
    const numValue = parseFloat(match[1])
    const unit = match[2]
    // Use 5x scaling for radius
    const scaledValue = numValue / 5
    return `${scaledValue}${unit}`
  }

  const scaleBorderWidth = (value: string | undefined) => {
    if (!value || value === "") return undefined
    const match = value.match(/(\d+\.?\d*)(px|%)/)
    if (!match) return value
    const numValue = parseFloat(match[1])
    const unit = match[2]
    // Use 1.3x scaling to match width scaling (same as button size scaling)
    // Admin width: 56px, deployed: 73px, ratio: 1.3x
    const scaledValue = numValue / 1.3
    return `${scaledValue}${unit}`
  }

  const borderStyle: React.CSSProperties = {}
  // Only apply border radius if explicitly set (not empty string)
  if (borderRadius && borderRadius !== "") {
    const scaledRadius = scaleBorderRadius(borderRadius)
    if (scaledRadius) borderStyle.borderRadius = scaledRadius
  }
  // Apply border if borderWidth is set
  if (borderWidth && borderWidth !== "") {
    const scaledBorderWidth = scaleBorderWidth(borderWidth)
    const borderWidthValue = scaledBorderWidth || borderWidth
    const borderColorValue = borderColor || textColor
    borderStyle.border = `${borderWidthValue} solid ${borderColorValue}`
  }

  const isProductImage =
    position === ButtonDisplayVariant.PRODUCT_IMAGE ||
    position === ButtonDisplayVariant.PRODUCT_IMAGE_LEFT ||
    position === ButtonDisplayVariant.PRODUCT_IMAGE_SIMPLIFIED ||
    position === ButtonDisplayVariant.PRODUCT_IMAGE_SIMPLIFIED_LEFT

  if (isProductImage) {
    if (iconTextSameLine && showText && !hasNoIcon)
      // Icon and text on same line for product image
      return (
        <div className="flex items-center">
          <div
            className="flex items-center justify-center"
            style={{
              padding: "8px 14px",
              gap: "4px",
              backgroundColor: color,
              borderRadius: borderStyle.borderRadius || "20px",
              ...borderStyle,
            }}
          >
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              {getButtonIconSVG(icon, "white")}
            </div>
            <div
              className="whitespace-nowrap flex items-center flex-shrink-0"
              style={{
                fontSize: "10px",
                fontWeight: "600",
                fontStyle: "normal",
                color: textColor,
                lineHeight: "1.2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                paddingTop: "1px",
              }}
            >
              {displayText}
            </div>
          </div>
        </div>
      )

    // Original product image layout (vertical)
    return (
      <div className="flex flex-col items-center">
        {!hasNoIcon && (
          <div
            className="flex items-center justify-center"
            style={{
              width: "48px",
              height: "32px",
              backgroundColor: color,
              marginBottom: showText ? "5px" : "0px",
              borderRadius: borderStyle.borderRadius || "12px",
              ...borderStyle,
            }}
          >
            <div className="w-3/5 h-3/5 flex items-center justify-center">
              {getButtonIconSVG(icon, "white")}
            </div>
          </div>
        )}
        {showText && (
          <div
            className="flex items-center justify-center"
            style={{
              height: "22px",
              backgroundColor: color,
              color: textColor,
              fontSize: "10px",
              fontWeight: "600",
              borderRadius: borderStyle.borderRadius || "12px",
              padding: "3px 5px",
              ...borderStyle,
            }}
          >
            {displayText}
          </div>
        )}
      </div>
    )
  }

  // Bottom corner layout
  if (iconTextSameLine && showText && !hasNoIcon)
    // Icon and text on same line for bottom corners
    return (
      <div className="flex items-center">
        <div
          className="flex items-center justify-center"
          style={{
            padding: "12px 16px",
            gap: "9.5px",
            backgroundColor: color,
            borderRadius: borderStyle.borderRadius || "25px",
            ...borderStyle,
          }}
        >
          <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
            {getButtonIconSVG(icon, "white")}
          </div>
          <div
            className="whitespace-nowrap flex items-center flex-shrink-0"
            style={{
              fontSize: "14px",
              fontWeight: "600",
              fontStyle: "normal",
              color: textColor,
              lineHeight: "1.2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              paddingTop: "1px",
            }}
          >
            {displayText}
          </div>
        </div>
      </div>
    )

  // Original bottom corner layout (vertical)
  return (
    <div className="flex flex-col items-center">
      {showText && (
        <div
          className="flex items-center justify-center"
          style={{
            width: "83px",
            height: "21px",
            backgroundColor: color,
            color: textColor,
            fontSize: "10px",
            fontWeight: "600",
            borderRadius: borderStyle.borderRadius || "10px",
            marginBottom: "5px",
            ...borderStyle,
          }}
        >
          {displayText}
        </div>
      )}
      {!hasNoIcon && (
        <div
          className="flex items-center justify-center"
          style={{
            width: "73px",
            height: "42px",
            backgroundColor: color,
            borderRadius: borderStyle.borderRadius || "21px",
            ...borderStyle,
          }}
        >
          <div className="w-3/5 h-3/5 flex items-center justify-center">
            {getButtonIconSVG(icon, "white")}
          </div>
        </div>
      )}
    </div>
  )
}
